// Briefing — one-time field-kit setup after activation.
// Primes the browser permissions with spy-flavoured explanations instead of
// ambushing the agent with raw permission prompts mid-mission.
import { el, mount, toast } from "../lib/dom";
import { currentUser } from "../lib/firebase";
import { requestPush, isIOS, isStandalone } from "../lib/permissions";
import { startSharing } from "../lib/location";
import { getPosition } from "../lib/geo";
import { installPrompt } from "../components/install-prompt";
import { go } from "../router";

const BRIEFED_KEY = "riverghost:briefed";

export function wasBriefed(): boolean {
  return localStorage.getItem(BRIEFED_KEY) === "1";
}

export async function renderBriefing(): Promise<void> {
  const uid = currentUser()!.uid;

  function done() {
    localStorage.setItem(BRIEFED_KEY, "1");
    go("/map");
  }

  // --- comms (push) ---
  const pushBtn = el("button.btn", {}, "Enable secure comms") as HTMLButtonElement;
  const pushNote = el("p.tiny.dim", {},
    isIOS() && !isStandalone()
      ? "iOS: install the field kit first (Share → Add to Home Screen), then enable comms from your Agent profile."
      : "CONTROL pings your lock screen when new intel drops.");
  pushBtn.addEventListener("click", async () => {
    pushBtn.disabled = true;
    const ok = await requestPush(uid);
    toast(ok ? "Secure comms online." : "Comms unavailable — the in-app feed still works.", ok ? "ok" : "warn");
    if (ok) pushBtn.textContent = "Secure comms ✓";
    else pushBtn.disabled = false;
  });

  // --- field tracking (GPS) ---
  const gpsBtn = el("button.btn", {}, "Enable field tracking") as HTMLButtonElement;
  gpsBtn.addEventListener("click", async () => {
    gpsBtn.disabled = true;
    gpsBtn.textContent = "Acquiring signal…";
    const p = await getPosition();
    if (p) {
      startSharing(uid, null, (reason) => {
        toast(reason === "denied"
          ? "Tracking denied. Re-enable it in your browser settings, then retry from your Agent profile."
          : "No GPS signal. Retry from your Agent profile.", "warn");
      });
      toast("Field tracking engaged.", "ok");
      gpsBtn.textContent = "Field tracking ✓";
    } else {
      toast("Location refused or unavailable. You can retry from your Agent profile.", "warn");
      gpsBtn.textContent = "Enable field tracking";
      gpsBtn.disabled = false;
    }
  });

  const startBtn = el("button.btn", { style: "margin-top:4px" }, "▶ Enter the field") as HTMLButtonElement;
  startBtn.addEventListener("click", done);

  mount(el("div", {},
    el("h2", {}, "// FIELD KIT SETUP"),
    el("div.card", {},
      el("span.card__tag", {}, "MISSION BRIEFING"),
      el("p", {}, "Agent, three tools keep you alive out there. Arm them now — or later from your Agent profile. Nothing here is mandatory."),
    ),
    el("div.card", {},
      el("span.card__tag", {}, "1 · SECURE COMMS"),
      pushNote,
      pushBtn,
    ),
    el("div.card", {},
      el("span.card__tag", {}, "2 · FIELD TRACKING"),
      el("p.tiny.dim", {}, "On the day, your position feeds the team map. Off-island it's only used to test your kit. You can stand down anytime."),
      gpsBtn,
    ),
    el("div.card", {},
      el("span.card__tag", {}, "3 · CAMERA"),
      el("p.tiny.dim", {}, "Selfie proofs and your spy avatar use the camera. The browser will ask the first time you shoot — say yes, Agent."),
    ),
    el("div.card", {},
      el("span.card__tag", {}, "4 · INSTALL"),
      el("p.tiny.dim", {}, "Add RIVERGHOST to your home screen for full-screen ops + lock-screen comms."),
      installPrompt(),
    ),
    el("div.card", {}, startBtn),
  ));
}
